'use client';

import { useBunja } from 'bunja/react';
import { useAtom, useAtomValue } from 'jotai';
import { useParams } from 'next/navigation';
import { useEffect } from 'react';

import { useOverlay } from '@flipster/overlay';
import { hexToRgb } from '@flipster/react/misc/color/rgb';
import { css } from '@flipster/styled-system/css';
import { CompetitionsEvent } from '@flipster/utils/segment/events/competitions';

import { LuckyDrawProvider, LuckyDrawsLayoutType, MissionsLayoutType } from '../../contentful/lucky-draw/state';
import { SegmentPage } from '../../misc/segment/segment-page';
import { registerCompetitionBunja } from './action';
import { CancelRegistration } from './cancel-registration';
import { Content } from './content';
import { Faq } from './faq';
import { Hero } from './hero';
import { MyInfo } from './my-info';
import { CompetitionItemFragment } from './query.generated';
import {
  CompetitionContentfulContext,
  CompetitionIdContext,
  EventTabValue,
  competitionBunja,
  competitionTabsBunja,
} from './state';

interface Props {
  competitionContentful: CompetitionItemFragment;
  luckyDraws?: LuckyDrawsLayoutType;
  missions?: MissionsLayoutType;
}

// URL 쿼리 파라미터에서 탭 값을 읽어 초기화하는 컴포넌트
const TabInitializer = () => {
  const { eventTabs, selectedEventTabValueAtom } = useBunja(competitionTabsBunja);
  const [, setSelectedEventTabValue] = useAtom(selectedEventTabValueAtom);

  useEffect(() => {
    const url = new URL(window.location.href);
    const tabFromUrl = url.searchParams.get('tab');
    if (tabFromUrl && eventTabs.some((tab) => tab.value === tabFromUrl)) {
      setSelectedEventTabValue(tabFromUrl as EventTabValue);
    }
  }, [eventTabs, setSelectedEventTabValue]);

  return null;
};

// URL 쿼리 파라미터에서 invitationCode를 읽어 자동 등록하는 컴포넌트
const InvitationCodeInitializer = () => {
  const { isRegisteredAtom } = useBunja(competitionBunja);
  const { registerCompetition } = useBunja(registerCompetitionBunja);
  const isRegistered = useAtomValue(isRegisteredAtom);

  const overlay = useOverlay();
  useEffect(() => {
    if (isRegistered) return;
    const url = new URL(window.location.href);
    const invitationCode = url.searchParams.get('invitationCode');
    if (!invitationCode) return;

    (async () => {
      try {
        await registerCompetition(invitationCode);
      } catch {
        // 에러는 mutation의 onError에서 처리됨
      }
    })();
  }, [isRegistered, registerCompetition, overlay]);

  return null;
};

export const CompetitionPage = ({ competitionContentful, luckyDraws, missions }: Props) => {
  const { id } = useParams<{ id: string }>();
  const { selectedEventTabValueAtom } = useBunja(competitionTabsBunja);
  const selectedEventTabValue = useAtomValue(selectedEventTabValueAtom);

  const variables: Record<string, string> = {
    '--key-color': `#${competitionContentful.keyColor}`,
    '--key-color-rgb': hexToRgb(`#${competitionContentful.keyColor}`),
  };

  return (
    <CompetitionIdContext value={id}>
      <CompetitionContentfulContext value={competitionContentful}>
        <SegmentPage name={CompetitionsEvent.CompetitionTradingMainPageView} context={{ competition_id: id }} />
        <TabInitializer />
        <InvitationCodeInitializer />
        <main style={variables} className={css({ mb: { base: 150, tablet: 96 } })}>
          <Hero />
          <MyInfo />
          <LuckyDrawProvider slug={id} luckyDraws={luckyDraws} missions={missions}>
            <Content key={selectedEventTabValue} />
          </LuckyDrawProvider>
          <Faq />
          <CancelRegistration />
        </main>
      </CompetitionContentfulContext>
    </CompetitionIdContext>
  );
};
